import { useId } from "react";
import { ImagePlus } from "lucide-react";
import { BackgroundLayer } from "../shield/BackgroundLayer";
import { ImageControls, useUploader } from "./ImageControls";
import { AiBackgroundGuide } from "./AiBackgroundGuide";
import { BackgroundGallery } from "./BackgroundGallery";
import { ColorField } from "./ColorField";
import { Switch } from "../ui/switch";
import { Slider } from "../ui/slider";
import { BACKGROUNDS, SHIELD_PATH, VIEWBOX } from "../../lib/shield";

const Thumb = ({ bg, background, colours, active, onPick }) => {
  const uid = useId().replace(/:/g, "");
  return (
    <button type="button" onClick={onPick} data-testid={`bg-option-${bg.id}`} aria-pressed={active}
      className={`group flex flex-col items-center gap-1.5 border-2 p-2 transition-[border-color,transform] hover:-translate-y-0.5 ${active ? "border-slate-900 bg-lime-50 shadow-[3px_3px_0_0_#0f172a]" : "border-slate-200 hover:border-slate-900"}`}>
      {bg.id === "upload" ? (
        <span className="flex h-[70px] w-[44px] items-center justify-center border-2 border-dashed border-slate-300 text-slate-500"><ImagePlus className="h-5 w-5" /></span>
      ) : (
        <svg viewBox={VIEWBOX} className="h-[70px] w-[50px]">
          <defs><clipPath id={`${uid}-c`}><path d={SHIELD_PATH} /></clipPath></defs>
          <g clipPath={`url(#${uid}-c)`}><BackgroundLayer background={{ ...background, style: bg.id }} colours={colours} uid={uid} /></g>
          <path d={SHIELD_PATH} fill="none" stroke={colours.border} strokeWidth="16" clipPath={`url(#${uid}-c)`} />
        </svg>
      )}
      <span className="text-center text-[11px] font-bold uppercase leading-tight tracking-wide">{bg.name}</span>
    </button>
  );
};

const Variant = ({ value, onChange }) => (
  <div className="flex gap-2" data-testid="bg-variant-toggle">
    {["vertical", "diagonal"].map((v) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`bg-variant-${v}`}
        className={`border-2 px-4 py-2 text-xs font-bold uppercase tracking-wide ${value === v ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-900"}`}>{v}</button>
    ))}
  </div>
);

const Overlay = ({ overlay, onChange }) => (
  <div className="space-y-3 border-t border-slate-200 pt-4">
    <label className="flex items-center justify-between text-sm font-bold">
      Colour overlay
      <Switch checked={overlay.enabled} onCheckedChange={(enabled) => onChange({ ...overlay, enabled })} data-testid="bg-overlay-switch" />
    </label>
    {overlay.enabled && (
      <>
        <ColorField id="overlay" label="Overlay colour" value={overlay.color} onChange={(color) => onChange({ ...overlay, color })} />
        <div>
          <div className="mb-2 flex justify-between text-xs font-bold uppercase tracking-wider text-slate-600"><span>Overlay strength</span><span>{Math.round(overlay.opacity * 100)}%</span></div>
          <Slider min={0.05} max={0.9} step={0.01} value={[overlay.opacity]} onValueChange={([opacity]) => onChange({ ...overlay, opacity })} data-testid="bg-overlay-opacity" />
        </div>
      </>
    )}
  </div>
);

export const BackgroundPanel = ({ background, colours, onChange }) => {
  const set = (patch) => onChange({ ...background, ...patch });
  const current = BACKGROUNDS.find((b) => b.id === background.style);
  const applyFile = (file) => set({ style: "upload", upload: { ...background.upload, file, crop: null, zoom: 1, x: 0, y: 0 } });
  const { busy, upload } = useUploader(applyFile);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-7 lg:grid-cols-4 xl:grid-cols-7">
        {BACKGROUNDS.map((bg) => (
          <Thumb key={bg.id} bg={bg} background={background} colours={colours} active={background.style === bg.id} onPick={() => set({ style: bg.id })} />
        ))}
      </div>
      <BackgroundGallery current={background.style === "upload" ? background.upload.file?.id : null} onPicked={applyFile} />
      <AiBackgroundGuide onFile={upload} busy={busy} />
      {current?.variants && <div><div className="eyebrow mb-2">Direction</div><Variant value={background.variant} onChange={(variant) => set({ variant })} /></div>}
      {background.style === "upload" && (
        <>
          <ImageControls placement={background.upload} label="background" aspect={400 / 560} minZoom={1} testPrefix="bg"
            onChange={(p) => set({ upload: { ...background.upload, ...p } })} />
          {background.upload.file && <Overlay overlay={background.overlay} onChange={(overlay) => set({ overlay })} />}
        </>
      )}
      <p className="text-xs text-slate-500">Backgrounds use your club colours. Switching style keeps your colours, player details and statistics.</p>
    </div>
  );
};
